export interface NetworkError {
  status?: number;
  response?: {
    body?: {
      message?: string;
      code?: string;
      errors?: Record<string, string>;
    };
  };
  responseJSON?: {
    message?: string;
  };
}
// Standard API error body that may carry field-level validation errors either as
// a map of field -> message or as a plain list of messages.
interface StandardErrorBody {
  message?: string;
  code?: string;
  errors?: Record<string, string> | string[];
}

const DEFAULT_ERROR_MESSAGE = "Request Failed";
const DEFAULT_ERROR_MESSAGE_CONNECTIVITY = "Unable to complete the request potentially due to network connectivity issue.";
const DEFAULT_ERROR_MESSAGE_FOR_FILE_DOWNLOAD = "Failed to download report.";
const DEFAULT_ERROR_MESSAGE_INTERNAL_SERVER_ERROR = "Internal Server Error";

/**
 * Selects how {@link getErrorMessage} formats and falls back when building a
 * user-facing message.
 *
 * In almost all cases you do NOT need to pass a strategy — the default `'auto'`
 * detects the error shape for you (jQuery-style `responseJSON` vs standard body,
 * and field errors as a map or a list) and shows every message in full. Only pass
 * an explicit strategy to override presentation/fallback:
 *
 * - `auto`         — (default) Detects everything. Handles `responseJSON` first,
 *                    then standard-body field errors (map OR list, multi-line),
 *                    then the top-level message. Right choice for essentially all
 *                    callers.
 * - `standard`     — Force the legacy single-line behavior: standard body only,
 *                    field-error map joined with `"; "`. Use when a surface must
 *                    not contain newlines.
 * - `multiLine`    — Standard body only (no `responseJSON`), field errors as a map
 *                    OR list rendered one per line.
 * - `nonStandard`  — `responseJSON` first, then single-line standard body.
 * - `fileDownload` — Like `nonStandard` but falls back to a download-specific
 *                    message when nothing can be extracted.
 */
export type ErrorMessageStrategy = 'auto' | 'standard' | 'multiLine' | 'nonStandard' | 'fileDownload';

/**
 * Formats field-level validation errors from the API response into a readable string.
 * Only displays the error messages, omitting redundant field names.
 * Example: { "phone_number": "Phone number must be..." } -> "Phone number must be..."
 * Multiple errors are joined with "; "
 */
export const formatValidationErrors = (errors: Record<string, string>): string => {
  return Object.values(errors).join('; ');
};

// --- Internal implementations -------------------------------------------------
// These hold the real logic. Both the parent dispatcher (`getErrorMessage`) and
// the deprecated public wrappers delegate here, so the parent never triggers the
// wrappers' deprecation warnings.

/** Standard API body, single-line field errors. */
const standardResponseMessage = (err: NetworkError | unknown, defaultErrorMsg?: string): string => {
  const networkErr = err as NetworkError;
  const message = networkErr?.response?.body?.message;
  if (networkErr?.status && message) {
    const {code: errorCode, errors: validationErrors} = networkErr.response!.body!;

    // If there are field-level validation errors, format and return them
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      return formatValidationErrors(validationErrors);
    }

    return errorCode ? `${message}. Code: ${errorCode}` : message;
  }
  return defaultErrorMsg || DEFAULT_ERROR_MESSAGE_CONNECTIVITY;
};

/** Standard API body, multi-line field errors (map or list). */
const multiLineStandardResponseMessage = (err: NetworkError | unknown, defaultErrorMsg?: string): string => {
  const body = (err as { response?: { body?: StandardErrorBody }; status?: number })?.response?.body;
  const status = (err as { status?: number })?.status;

  if (status && body) {
    const {message: errorMessage, code: errorCode, errors: validationErrors} = body;

    // Surface field-level validation errors first so a generic top-level message
    // (e.g. "Request data validation failed") never hides the specific errors.
    const messages = Array.isArray(validationErrors)
      ? validationErrors
      : validationErrors
        ? Object.values(validationErrors)
        : [];
    const filtered = messages.filter(Boolean);
    if (filtered.length > 0) {
      return filtered.join('\n');
    }

    if (errorMessage) {
      return errorCode ? `${errorMessage}. Code: ${errorCode}` : errorMessage;
    }
  }
  return defaultErrorMsg || DEFAULT_ERROR_MESSAGE_CONNECTIVITY;
};

/** jQuery-style `responseJSON` first, then standard body. */
const nonStandardResponseMessage = (err: NetworkError | unknown, defaultErrorMsg?: string): string => {
  const e = err as NetworkError;
  if (e?.responseJSON) {
    return e.responseJSON.message?.trim() || DEFAULT_ERROR_MESSAGE_INTERNAL_SERVER_ERROR;
  }
  return standardResponseMessage(err, defaultErrorMsg || DEFAULT_ERROR_MESSAGE);
};

/**
 * Auto-detecting strategy: inspects the error shape so callers don't have to.
 * 1. jQuery-style `responseJSON` → use its message.
 * 2. Standard API body → surface field-level errors (map OR list) in full,
 *    falling back to the top-level message/code.
 * 3. Otherwise → the provided default (or "Request Failed").
 */
const autoResponseMessage = (err: NetworkError | unknown, defaultErrorMsg?: string): string => {
  const e = err as NetworkError;
  if (e?.responseJSON) {
    return e.responseJSON.message?.trim() || DEFAULT_ERROR_MESSAGE_INTERNAL_SERVER_ERROR;
  }
  return multiLineStandardResponseMessage(err, defaultErrorMsg || DEFAULT_ERROR_MESSAGE);
};

// --- Parent dispatcher --------------------------------------------------------

/**
 * Single entry point for building a user-facing error message from an API error.
 *
 * Prefer this over the individual `getErrorMessageFor*` helpers — they are
 * retained for backward compatibility and are now deprecated. Over time, all
 * call sites should migrate to `getErrorMessage`, after which the wrappers can
 * be removed.
 *
 * You usually only need `getErrorMessage(err, 'My fallback message')` — the
 * default `'auto'` strategy detects the error shape for you. Pass an explicit
 * {@link ErrorMessageStrategy} only to override presentation/fallback.
 *
 * @param err the caught error (superagent/jQuery-style response or unknown)
 * @param defaultErrorMsg fallback message when none can be extracted; when omitted,
 *   a sensible per-strategy default is used
 * @param strategy how to format/fall back (see {@link ErrorMessageStrategy});
 *   defaults to `'auto'`, which detects the shape — suitable for nearly all callers
 *
 * @example
 * // Recommended — auto-detects responseJSON vs standard body and field errors:
 * snackbarShowMessage(getErrorMessage(err, 'Failed to load bookings'), 'error');
 * @example
 * // Override: force legacy single-line formatting (no newlines):
 * toast.error(getErrorMessage(err, 'Could not save', 'standard'));
 * @example
 * // Override: download-specific fallback when no message can be extracted:
 * toast.error(getErrorMessage(err, undefined, 'fileDownload'));
 */
export const getErrorMessage = (
  err: NetworkError | unknown,
  defaultErrorMsg?: string,
  strategy: ErrorMessageStrategy = 'auto'
): string => {
  switch (strategy) {
    case 'standard':
      return standardResponseMessage(err, defaultErrorMsg);
    case 'multiLine':
      return multiLineStandardResponseMessage(err, defaultErrorMsg);
    case 'fileDownload':
      return nonStandardResponseMessage(err, defaultErrorMsg || DEFAULT_ERROR_MESSAGE_FOR_FILE_DOWNLOAD);
    case 'nonStandard':
      return nonStandardResponseMessage(err, defaultErrorMsg);
    case 'auto':
    default:
      return autoResponseMessage(err, defaultErrorMsg);
  }
};

// --- Deprecated public wrappers ----------------------------------------------
// Kept so existing call sites keep working. New code should call `getErrorMessage`
// with the appropriate strategy; these will be removed once all uses migrate.

/**
 * Error code returned when a phone number could not be verified for SMS.
 *
 * Mirrors {@code ErrorCode.SMS_VERIFICATION_FAILED} in the API. The API deliberately commits the
 * surrounding save before responding with this, so it marks a partial success: the user or
 * location manager was persisted and only text notifications could not be enabled. Callers should
 * treat it as a warning and complete their success path (close the form, refresh the list) rather
 * than as a failed request.
 */
export const SMS_VERIFICATION_FAILED_CODE = '190001';

/** True when a request failed solely because the phone number could not be verified for SMS. */
export const isSmsVerificationFailure = (err: NetworkError | unknown): boolean =>
  (err as NetworkError)?.response?.body?.code === SMS_VERIFICATION_FAILED_CODE;

/** Mirrors {@code ErrorCode.INSUFFICIENT_LOCATION_CAPACITY} in the API. */
export const INSUFFICIENT_LOCATION_CAPACITY_CODE = '20014';

/** True when a booking request was rejected because the location lacks capacity for the dates. */
export const isInsufficientLocationCapacity = (err: NetworkError | unknown): boolean =>
  (err as NetworkError)?.response?.body?.code === INSUFFICIENT_LOCATION_CAPACITY_CODE;

/** Mirrors {@code ErrorCode.CAPACITY_SCHEDULE_CONFLICT} in the API. */
export const CAPACITY_SCHEDULE_CONFLICT_CODE = '10021';

/** True when a capacity schedule save was rejected because someone else saved the schedule first. */
export const isCapacityScheduleConflict = (err: NetworkError | unknown): boolean =>
  (err as NetworkError)?.response?.body?.code === CAPACITY_SCHEDULE_CONFLICT_CODE;

/**
 * @deprecated Use `getErrorMessage(err, defaultErrorMsg, 'standard')` instead.
 * Retained for backward compatibility; will be removed once all call sites migrate.
 */
export const getErrorMessageForStandardResponse = (err: NetworkError | unknown, defaultErrorMsg: string) => {
  return standardResponseMessage(err, defaultErrorMsg);
};

/**
 * @deprecated Use `getErrorMessage(err, defaultErrorMsg, 'nonStandard')` instead.
 * Retained for backward compatibility; will be removed once all call sites migrate.
 */
export const getErrorMessageForNonStandardAndStandardResponse = (err: NetworkError | any, defaultErrorMsg?: string) => {
  return nonStandardResponseMessage(err, defaultErrorMsg);
};

/**
 * @deprecated Use `getErrorMessage(err, defaultErrorMsg, 'fileDownload')` instead.
 * Retained for backward compatibility; will be removed once all call sites migrate.
 */
export const getErrorMessageForFileDownloads = (err: NetworkError | any, defaultErrorMsg?: string) => {
  return nonStandardResponseMessage(err, defaultErrorMsg || DEFAULT_ERROR_MESSAGE_FOR_FILE_DOWNLOAD);
};
